<template>
  <!-- empty top element enables adding styles and classes from the using component (like padding) without disturbing the wrapper -->
  <div>
    <div class="img-wrapper" :class="{ square }" :style="wrapperStyle">
      <img
        ref="productImage"
        :src="src"
        :class="{
          'blend-mode-multiply': blend,
        }"
        :style="imgStyle"
        :loading="loading"
        @load="emit('load', $event), (hasLoaded = true)"
      />

      <img v-if="!hasLoaded" :src="placeholderImg" />

      <div v-if="slots.overlay" class="overlay" :style="`opacity: ${defaultOverlayOpacity}`">
        <slot name="overlay" />
      </div>

      <div v-if="label?.text" class="label" :style="`opacity: ${label.defaultOpacity}`" :title="label.text">
        {{ label.text }}
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ProductFallbackImage } from '@/models/Image'
import { computed, onActivated, onDeactivated, onMounted, onUnmounted, ref, StyleValue, useSlots } from 'vue'

// -----------
// -- Setup --
// -----------
const emit = defineEmits(['error', 'load'])

const props = withDefaults(
  defineProps<{
    src?: string
    width?: string
    height?: string
    maxWidth?: string
    maxHeight?: string
    square?: boolean
    blend?: boolean
    defaultOverlayOpacity?: number
    label?: {
      text: string
      defaultOpacity: number
    }
    loading?: 'lazy' | 'eager'
    fallbackImg?: string | null
  }>(),
  { defaultOverlayOpacity: 0.5, loading: 'lazy', fallbackImg: ProductFallbackImage, blend: true },
)

const slots = useSlots()

const placeholderImg = '/images/img-placeholder.png' // needed for iPad. uses up a small amount of height which makes the loaded img claim all necessary space

// ---------------
// -- Reactives --
// ---------------
const productImage = ref<HTMLImageElement>()
const hasLoaded = ref(false)

const wrapperStyle = computed(
  (): StyleValue => ({
    width: props.width,
    height: props.height,
  }),
)

const imgStyle = computed(
  (): StyleValue => ({
    maxWidth: props.maxWidth || (props.square ? props.maxHeight : undefined),
    maxHeight: props.maxHeight || (props.square ? props.maxWidth : undefined),
  }),
)

// ---------------------
// -- Lifecycle hooks --
// ---------------------
onMounted(() => productImage.value?.addEventListener('error', errorHandler, { once: true }))
onActivated(() => productImage.value?.addEventListener('error', errorHandler, { once: true }))
onUnmounted(() => productImage.value?.removeEventListener('error', errorHandler))
onDeactivated(() => productImage.value?.removeEventListener('error', errorHandler))

// -------------
// -- Methods --
// -------------
const errorHandler = (event: Event) => {
  emit('error', event)
  const target = event.target as HTMLImageElement

  if (target) {
    if (props.fallbackImg) {
      target.src = props.fallbackImg
    } else {
      target.style.visibility = 'hidden' // hide broken img icon
    }
  }
}
</script>

<style lang="scss" scoped>
.img-wrapper {
  position: relative;

  &:hover > * {
    opacity: 1 !important;
  }

  img {
    object-fit: scale-down;
    max-width: 100%;
    max-height: 100%;
  }

  &.square {
    &::before {
      content: '';
      display: block;
      padding-top: 100% !important; // makes height equal to width
    }

    img {
      position: absolute !important;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);

      width: 100%;
      height: 100%;
    }
  }

  .overlay {
    z-index: 1;
    position: absolute;
    display: flex;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  .label {
    z-index: 2;
    display: flex;
    position: absolute;
    font-size: 0.9em;

    background-color: white;
    border-radius: 2px 5px;
    border: 1px solid var(--light-grey);
    padding: 3px;
    max-height: 4em;
    min-width: 5em;
    bottom: 1.5em;

    max-width: 100%;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  &::after {
    content: attr(data-title);
    position: absolute;
    color: white;
    font-size: 1.2em;
    bottom: 8%;
    font-weight: bold;
    text-shadow: 1px 2px 4px black;
    width: 100%;
    height: 1em;
    display: flex;
    justify-content: center;
    text-decoration: none !important;
  }
}

.blend-mode-multiply {
  mix-blend-mode: multiply;
}

:deep() {
  .overlay {
    position: relative; // TODO remove?

    > * {
      display: flex;
    }

    .top {
      align-items: flex-start;
      align-self: flex-start;
    }
    .middle {
      align-items: center;
      align-self: center;
    }
    .bottom {
      align-items: flex-end;
      align-self: flex-end;
    }

    .left {
      margin-left: 0;
      justify-content: left;
      margin-right: auto;
    }
    .center {
      justify-content: center;
      margin-left: auto;
      margin-right: auto;
    }
    .right {
      justify-content: right;
      margin-left: auto;
      margin-right: 0;
    }
  }
}
</style>
